/**
 * UserLocationLayer - 在地圖上顯示使用者目前位置（藍點）與「定位」按鈕
 *
 * 位置狀態刻意放在這個獨立元件，不放進 MapContent：MapContent 每次 re-render 都會重新
 * fitBounds，若定位更新觸發它 re-render，地圖視野會一直被拉回行程範圍。
 *
 * 權限策略：已授權過定位的使用者進地圖就直接顯示藍點；尚未授權的不主動跳權限詢問，
 * 等使用者按下定位按鈕才請求。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AdvancedMarker, ControlPosition, MapControl, useMap } from '@vis.gl/react-google-maps';
import { LocateFixed, LocateOff } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

type LatLng = { lat: number; lng: number };

export default function UserLocationLayer() {
  const map = useMap();
  const { showToast } = useToast();
  const [position, setPosition] = useState<LatLng | null>(null);
  const [isDenied, setIsDenied] = useState(false);
  const watchIdRef = useRef<number | null>(null);
  // 按下定位按鈕但還沒拿到第一筆位置時，拿到後要把地圖移過去
  const panOnNextFixRef = useRef(false);

  const startWatching = useCallback(() => {
    if (watchIdRef.current !== null || !('geolocation' in navigator)) return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setPosition(next);
        setIsDenied(false);
        if (panOnNextFixRef.current && map) {
          panOnNextFixRef.current = false;
          map.panTo(next);
          map.setZoom(16);
        }
      },
      (err) => {
        const wasRequested = panOnNextFixRef.current;
        panOnNextFixRef.current = false;
        if (err.code === err.PERMISSION_DENIED) {
          setIsDenied(true);
          setPosition(null);
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
          }
          if (wasRequested) showToast('warning', '無法取得定位，請到瀏覽器設定允許此網站使用位置資訊');
        } else if (wasRequested) {
          showToast('warning', '暫時無法取得目前位置，請稍後再試');
        }
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
  }, [map, showToast]);

  // 已授權過的使用者自動開始追蹤；permissions API 不支援的瀏覽器（舊版 Safari）就等按鈕
  useEffect(() => {
    let cancelled = false;
    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((status) => {
        if (cancelled) return;
        if (status.state === 'granted') startWatching();
        if (status.state === 'denied') setIsDenied(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [startWatching]);

  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  const handleLocate = () => {
    if (!('geolocation' in navigator)) {
      showToast('warning', '此裝置不支援定位功能');
      return;
    }
    if (position && map) {
      map.panTo(position);
      map.setZoom(16);
      return;
    }
    panOnNextFixRef.current = true;
    // 先前被拒絕時重新呼叫一次，讓瀏覽器有機會重新詢問（或回報錯誤給使用者）
    if (watchIdRef.current === null) {
      startWatching();
    }
  };

  return (
    <>
      {position && (
        <AdvancedMarker position={position} zIndex={1000} clickable={false}>
          <div style={{ position: 'relative', width: 18, height: 18 }}>
            <span
              className="animate-ping"
              style={{
                position: 'absolute',
                inset: -6,
                borderRadius: '50%',
                background: 'rgba(66,133,244,0.35)',
              }}
            />
            <span
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: '#4285F4',
                border: '3px solid #fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
              }}
            />
          </div>
        </AdvancedMarker>
      )}

      <MapControl position={ControlPosition.RIGHT_BOTTOM}>
        <button
          type="button"
          onClick={handleLocate}
          aria-label="顯示目前位置"
          title="顯示目前位置"
          className="m-3 flex size-10 items-center justify-center rounded-full bg-white shadow-md hover:bg-slate-50 transition-colors"
        >
          {isDenied ? (
            <LocateOff size={20} className="text-slate-400" />
          ) : (
            <LocateFixed size={20} className={position ? 'text-[#4285F4]' : 'text-slate-600'} />
          )}
        </button>
      </MapControl>
    </>
  );
}
